import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toastr } from 'react-redux-toastr';
import { ApiRequestError, ApiServerError } from '~/fetch';
import type { OverrideNetUnitRequest, ProductNetUnits } from '~/ogm-hub';
import { OptimizationSuggestionController } from '~/ogm-hub';
import type { IOverrideNetUnitForm } from '~/OGMAI/Components/EditNetUnitsModal';
import type { IAppState } from '~/appstate';
import { User } from '../../helpers';

export interface IEditNetUnitsState {
	isSaveEditedNetUnitsProcessing: boolean;
	isOpen: boolean;
	productNetUnits: ProductNetUnits[];
	netUnitEditRequests: Record<string, IOverrideNetUnitForm>;
	existingNetUnits: ProductNetUnits[];
}

const initialState: IEditNetUnitsState = {
	isSaveEditedNetUnitsProcessing: false,
	isOpen: false,
	productNetUnits: [],
	netUnitEditRequests: {},
	existingNetUnits: [],
};

export const setExistingNetUnits = createAsyncThunk<
	ProductNetUnits[],
	ProductNetUnits[] | null,
	{ state: IAppState; rejectValue: string[] }
>('suggestionsOverview/setExistingNetUnits', async (netUnits, { getState }) => {
	const state = getState();
	if (netUnits.length) {
		return netUnits;
	}

	return state.metaProductPage.page.metaProduct.products.reduce(
		(agg: ProductNetUnits[], m) => {
			const entity =
				state.productHeaders.entities[
					`${m.corpSupplierCode}|${m.supplierProductNumber}`
				];
			const prices =
				state.productHeaderPrices.entities[
					`${m.corpSupplierCode}|${m.supplierProductNumber}`
				];

			if (entity != null && prices != null && prices.prices != null) {
				prices.prices.forEach((price) => {
					agg.push({
						corpSupplierCode: entity.corpSupplierCode,
						productNumber: entity.supplierProductNumber,
						unitOfMeasure: price.unitOfMeasure,
						productName: entity.description,
						packSize: entity.packSize,
						netUnits: price.unitDivisor,
						netUnitType: price.netUnitsType,
					} as ProductNetUnits);
				});
			}

			return agg;
		},
		[]
	);
});

export const saveEditedNetUnits = createAsyncThunk<
	void,
	void,
	{ state: IAppState }
>(
	'suggestionsOverview/saveEditedNetUnits',
	async (_: void, { rejectWithValue, getState }) => {
		try {
			const state = getState();
			const existingNetUnits = state.EditNetUnits.productNetUnits;
			const newNetUnits = state.EditNetUnits.netUnitEditRequests;
			const metaProductId = state.metaProductPage.page.metaProduct.id;

			const requests = existingNetUnits.reduce(
				(acc: OverrideNetUnitRequest[], productNetUnit) => {
					const key = generateNetUnitEditRequestKey(
						productNetUnit.corpSupplierCode,
						productNetUnit.productNumber,
						productNetUnit.unitOfMeasure
					);
					const newNetUnit = newNetUnits[key];

					if (
						newNetUnit &&
						(newNetUnit.netUnits !== productNetUnit.netUnits ||
							newNetUnit.netUnitType?.value !== productNetUnit.netUnitType) &&
						newNetUnit.netUnits != undefined &&
						newNetUnit.netUnitType?.value != undefined
					) {
						acc.push({
							corpSupplierCode: newNetUnit.corpSupplierCode,
							supplierProductNumber: newNetUnit.supplierProductNumber,
							unitMeasureCode: newNetUnit.unitMeasureCode,
							netUnits: newNetUnit.netUnits,
							netUnitType: newNetUnit.netUnitType.value,
						});
					}

					return acc;
				},
				[]
			);

			let isFullyPopulated = true;
			Object.keys(newNetUnits).forEach((newNetUnitKey) => {
				const newNetUnit = newNetUnits[newNetUnitKey];

				if (
					newNetUnit.netUnits == undefined ||
					newNetUnit.netUnitType?.value == undefined
				) {
					toastr.error(
						'Error!',
						`Net unit details for item ${newNetUnit.supplierProductNumber} ${
							newNetUnit.unitMeasureCode
						} are incomplete.  Please enter the ${
							newNetUnit.netUnits == undefined ? 'Net Units' : 'Net Unit Type'
						} and resubmit.`
					);
					isFullyPopulated = false;
				}
			});
			if (!isFullyPopulated) {
				return rejectWithValue('Any edited net unit must be fully populated.');
			}

			if (requests.length === 0) {
				toastr.error(
					'Error!',
					'At least one product must have a net unit override entered. Please update the incorrect net unit(s) and resubmit.'
				);
				return rejectWithValue(
					'Must edit at least one net unit and populate it fully.'
				);
			}
			if (User.isProcurementServicesUser()) {
				const response =
					await OptimizationSuggestionController.OverrideNetUnits({
						requests,
					});
				if (!response.ok || response.value.errors.length !== 0) {
					toastr.error(
						'Error!',
						'An error occured while saving the net units.'
					);
				}
			} else {
				const response =
					await OptimizationSuggestionController.UpdateOptimizationItemNetUnits(
						{
							metaProductId,
							requests,
						}
					);
				if (!response.ok || response.value.errors.length !== 0) {
					toastr.error(
						'Error!',
						'An error occured while saving the net units.'
					);
				}
			}
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export const generateNetUnitEditRequestKey = (
	corpSupplierCode: string,
	supplierProductNumber: string,
	unitMeasureCode: string
) => {
	return `${corpSupplierCode}~${supplierProductNumber}~${unitMeasureCode}`;
};

export const EditNetUnitsSlice = createSlice({
	name: 'EditNetUnits',
	initialState,
	reducers: {
		onEditNetUnits: (state, action) => {
			state.existingNetUnits = action.payload;
			state.productNetUnits = action.payload;
			state.isOpen = true;
		},
		onEditNetUnitsCancel: (state) => {
			state.isOpen = false;
			state.existingNetUnits = [];
			state.productNetUnits = [];
		},
		onNetUnitChanged: (state, action) => {
			const request = action.payload;
			const requestKey = generateNetUnitEditRequestKey(
				request.corpSupplierCode,
				request.supplierProductNumber,
				request.unitMeasureCode
			);

			state.netUnitEditRequests[requestKey] = request;
		},
		initNetUnits: (state, netUnits) => {
			state.productNetUnits = netUnits.payload;
		},
	},
	extraReducers: (builder) => {
		builder.addCase(saveEditedNetUnits.fulfilled, (state) => {
			state.isSaveEditedNetUnitsProcessing = false;
			state.isOpen = false;
		});
		builder.addCase(saveEditedNetUnits.pending, (state) => {
			state.isSaveEditedNetUnitsProcessing = true;
		});
		builder.addCase(saveEditedNetUnits.rejected, (state) => {
			state.isSaveEditedNetUnitsProcessing = false;
		});
		builder.addCase(setExistingNetUnits.fulfilled, (state, action) => {
			state.productNetUnits = action.payload;
			state.existingNetUnits = action.payload;
		});
	},
});

export const {
	onEditNetUnits,
	onEditNetUnitsCancel,
	onNetUnitChanged,
	initNetUnits,
} = EditNetUnitsSlice.actions;
